import { Observable, Subscription } from 'rxjs';

/**
 * Helper class to manage socket subscriptions with automatic fallback polling
 *
 * This ensures UI never gets stuck even if socket events are missed due to:
 * - Network disconnection
 * - Page refresh
 * - Socket connection issues
 *
 * Usage:
 * ```typescript
 * const helper = new SocketWithFallback(
 *   socketService.onStatementExtractionStatus$,
 *   () => statementService.getStatementStatus(id),
 *   (data) => data.extraction_status === 'READY',
 *   () => this.handleComplete()
 * );
 * ```
 */
export class SocketWithFallback<T> {
  private socketSub?: Subscription;
  private fallbackTimer?: any;
  private readonly pollInterval: number;

  constructor(
    private socketEvent$: Observable<T>,
    private fallbackFn: () => Observable<any>,
    private checkCondition: (data: any) => boolean,
    private onComplete: () => void,
    private onError?: () => void,
    pollInterval: number = 45000
  ) {
    this.pollInterval = pollInterval;
    this.start();
  }

  /**
   * Start listening to both socket events and fallback polling
   */
  private start(): void {
    // Listen to socket events
    this.socketSub = this.socketEvent$.subscribe((data) => {
      if (this.checkCondition(data)) {
        this.onComplete();
        this.cleanup();
      }
    });

    // Start fallback polling
    this.fallbackTimer = setInterval(() => {
      this.fallbackFn().subscribe({
        next: (result) => {
          if (this.checkCondition(result.data || result)) {
            this.onComplete();
            this.cleanup();
          }
        },
        error: () => {
          if (this.onError) {
            this.onError();
          }
          this.cleanup();
        }
      });
    }, this.pollInterval);
  }

  /**
   * Stop all subscriptions and timers
   */
  cleanup(): void {
    if (this.socketSub) {
      this.socketSub.unsubscribe();
      this.socketSub = undefined;
    }
    if (this.fallbackTimer) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = undefined;
    }
  }
}
